import React, { FC, JSX, useCallback } from "react";
import { compose } from "redux";
import { withRouter, InboxRow, RouterProps } from "component-library";
import { UrlCommand } from "shared/config/UrlCommand";

import { USE_BROWSER_BACK_KEY } from "config/common";

import {
  FilterType,
  IJobSender,
} from "hoc/withJobsFilter/withJobsFilter.config";
import withPagination, { IWithPaginationProps } from "hoc/withPagination";
import {
  IJobTableItem,
  IJobTableItemTag,
  JobTableItemUuid,
} from "components/JobsTable/JobsTable.models";

import SelectAllControl, {
  ISelectAllControlTexts,
} from "components/SelectAllControl/SelectAllControl";
import "./JobsTable.scss";
import { createSearchParams } from "react-router-dom";
import { SetJobsFeedData, IJobsFeedState } from "store/jobsFeed/jobsFeed.types";
import { DEFAULT_JOBS_FEEED } from "shared/helpers/common";
import {
  JobsFeedUsageEvents,
  sendJobsfeedUsageHeapEventWithUserPrefix,
} from "shared-services";

export interface IJobsTableTexts {
  resultStats: string;
  refreshResultsLabel: string;
  localHire: string;
  selectAllControlTexts: ISelectAllControlTexts;
}

export interface JobsTableProps extends Omit<
  IWithPaginationProps,
  "renderPagination" | "renderPaginationResultStats"
> {
  texts: IJobsTableTexts;
  items: IJobTableItem[];
  selectedItems: JobTableItemUuid[];
  returnUri: string;
  deselectItems: (itemIds: JobTableItemUuid[]) => void;
  selectItems: (itemIds: JobTableItemUuid[]) => void;
  onItemClick?: (item: IJobTableItem, goToCallback?: () => void) => void;
  onSenderClick?: (sender: IJobSender) => void;
  onTagClick?: (tag: IJobTableItemTag) => void;
  onMarkAsRead: (itemIds: JobTableItemUuid[]) => void;
  onMarkAsUnread: (itemIds: JobTableItemUuid[]) => void;
  onRefresh: () => void;
  isPerformer?: boolean;
  jobsFeed?: IJobsFeedState;
  setJobsFeedData?: SetJobsFeedData;
  showSelectAllControl?: boolean;
}

type IJobsTableComposedProps = JobsTableProps &
  IWithPaginationProps &
  RouterProps;

const Sender = ({
  sender,
  onClick,
}: {
  sender: IJobSender;
  onClick: (e: React.MouseEvent<HTMLAnchorElement>, sender: IJobSender) => void;
}) => {
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      onClick(e, sender);
    },
    [onClick, sender]
  );

  return (
    <a href="#" onClick={handleClick} tabIndex={0}>
      {sender.name}
    </a>
  );
};

interface TagProps {
  tag: IJobTableItemTag;
  onClick: (
    e: React.MouseEvent<HTMLAnchorElement>,
    tag: IJobTableItemTag
  ) => void;
  prefix?: string;
  renderTagName?: () => JSX.Element;
}

const Tag: FC<React.PropsWithChildren<TagProps>> = ({
  tag,
  onClick,
  prefix = "#",
  renderTagName,
}) => {
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      onClick(e, tag);
    },
    [onClick, tag]
  );

  return (
    <a href="#" onClick={handleClick} tabIndex={0}>
      {renderTagName ? (
        renderTagName()
      ) : (
        <>
          {prefix}
          {tag.name}
        </>
      )}
    </a>
  );
};

export class JobsTable extends React.PureComponent<IJobsTableComposedProps> {
  private readonly scrollRef: React.RefObject<HTMLDivElement> =
    React.createRef();

  componentDidMount(): void {
    this.scrollToElement();
  }

  componentDidUpdate(): void {
    this.scrollToElement();
  }

  private scrollToElement() {
    if (this.scrollRef.current) {
      this.scrollRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      this.props.setJobsFeedData?.({
        pathname: this.props.jobsFeed?.pathname || DEFAULT_JOBS_FEEED.PATHNAME,
        pageNumber:
          this.props.jobsFeed?.pageNumber || DEFAULT_JOBS_FEEED.PAGENUMBER,
        breakdownId: DEFAULT_JOBS_FEEED.BREAKDOWN_ID,
        isBackToJobsFeed: DEFAULT_JOBS_FEEED.IS_BACK_TO_JOBS_FEED,
      });
    }
  }

  public render() {
    const { items, showSelectAllControl = true } = this.props;

    return (
      <section className="c-jobs-table">
        {showSelectAllControl && this.renderSelectAllControl()}
        <table className="c-jobs-table__body">
          {items.map((item: IJobTableItem) => this.renderListItem(item))}
        </table>
        {this.renderBottomBar()}
      </section>
    );
  }

  private renderListItem = (item: IJobTableItem) => {
    const { selectedItems } = this.props;
    const isSelected = selectedItems.includes(item.id);
    const itemId = item.id;
    const isMatch =
      itemId === this.props.jobsFeed?.breakdownId.replace(/^"|"$/g, "");

    return (
      <div key={item.id} {...(isMatch ? { ref: this.scrollRef } : {})}>
        <InboxRow
          key={item.id}
          sender={this.renderSender(item)}
          isChecked={isSelected}
          value={item.id}
          title={item.title}
          additionalInfo={item.additionalInfo}
          isRead={item.isRead}
          labels={item.labels}
          onClick={this.handleClick(item)}
          onSelect={this.handleSelect(isSelected, item.id)}
          tags={this.renderTags(item)}
          url={this.getItemUrl(item)}
        />
      </div>
    );
  };

  private renderSender(item: IJobTableItem) {
    if (!item.sender.id) {
      return item.sender.name;
    }

    return <Sender sender={item.sender} onClick={this.handleSenderClick} />;
  }

  private renderTags(item: IJobTableItem) {
    const { texts } = this.props;

    return item.tags.map((tag) => {
      const renderTagName =
        tag.filterType === FilterType.Locations
          ? () => (
              <>
                <span className="text-color-brand-one">#{texts.localHire}</span>{" "}
                ({tag.name})
              </>
            )
          : undefined;

      return (
        <Tag
          // eslint-disable-next-line
          key={`${tag.facetCategory}_${tag.facetId}`} //faceId is number
          tag={tag}
          onClick={this.handleTagClick}
          prefix={item.tagPrefix}
          renderTagName={renderTagName}
        />
      );
    });
  }

  public renderBottomBar() {
    const { renderPagination } = this.props;

    return (
      <div className="c-jobs-table__bottom-bar">
        <div className="c-jobs-table__pagination">{renderPagination(true)}</div>
      </div>
    );
  }

  public renderSelectAllControl() {
    return (
      <SelectAllControl
        selectItems={this.props.selectItems}
        deselectItems={this.props.deselectItems}
        selectedItems={this.props.selectedItems}
        markItemsAsRead={this.markItemsAsRead}
        markItemsAsUnread={this.markItemsAsUnread}
        items={this.props.items}
        onSelectAllClick={() =>
          sendJobsfeedUsageHeapEventWithUserPrefix(
            JobsFeedUsageEvents.SelectAll
          )
        }
      />
    );
  }

  private handleSelect = (isSelected: boolean, id: JobTableItemUuid) => () => {
    if (isSelected) {
      this.props.deselectItems([id]);
    } else {
      this.props.selectItems([id]);
    }
  };

  private handleClick =
    (item: IJobTableItem) => (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (e.ctrlKey || e.metaKey) {
        return;
      }

      e.preventDefault();

      const params = new URLSearchParams(window.location.search);
      const pageNumber = params.get("page");

      this.props.setJobsFeedData?.({
        pathname: this.props.jobsFeed?.pathname || DEFAULT_JOBS_FEEED.PATHNAME,
        pageNumber: pageNumber || DEFAULT_JOBS_FEEED.PAGENUMBER,
        breakdownId: item.id,
        isBackToJobsFeed: DEFAULT_JOBS_FEEED.IS_BACK_TO_JOBS_FEED,
      });

      const goTo = () => {
        const { router } = this.props;

        const searchParams = createSearchParams({
          [UrlCommand.ReturnUri]: this.getReturnUri(),
        }).toString();

        if (item.url) {
          router.navigate(`${item.url}?${searchParams}`, {
            state: { [USE_BROWSER_BACK_KEY]: true },
          });
        }
      };

      if (this.props.onItemClick) {
        this.props.onItemClick(item, goTo);
      } else {
        goTo();
      }
    };

  private handleSenderClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    sender: IJobSender
  ) => {
    e.preventDefault();
    e.stopPropagation();
    this.props.onSenderClick?.(sender);
  };

  private handleTagClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    tag: IJobTableItemTag
  ) => {
    e.preventDefault();
    e.stopPropagation();
    this.props.onTagClick?.(tag);
  };

  private markItemsAsRead = (itemIds: string[]) => {
    this.props.onMarkAsRead(itemIds);
  };

  private markItemsAsUnread = (itemIds: string[]) => {
    this.props.onMarkAsUnread(itemIds);
  };

  private getReturnUri() {
    return encodeURIComponent(this.props.returnUri);
  }

  private getItemUrl(item: IJobTableItem) {
    return `${item.url}?${UrlCommand.ReturnUri}=${this.getReturnUri()}`;
  }
}

export default compose(
  withRouter,
  withPagination
)(JobsTable) as React.ComponentType<JobsTableProps>;
